"use client"

import { useId } from "react"
import { useField } from "@payloadcms/ui"
import type { DateFieldClientComponent } from "payload"

import { Input } from "@/components/ui/input"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Date field.
 *
 * Uses the native `<input type="datetime-local">` (or `date` when the
 * field's pickerAppearance is `dayOnly`) — keeps a11y + keyboard +
 * mobile UI for free, no react-day-picker dep needed. The value flows
 * through Payload as an ISO 8601 string just like the stock DateField.
 */
export const ShadcnDateField: DateFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<string>({
    path: path ?? fieldName,
  })
  const id = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)

  const pickerAppearance = (field as {
    admin?: { date?: { pickerAppearance?: string } }
  })?.admin?.date?.pickerAppearance
  const dateOnly = pickerAppearance === "dayOnly" || pickerAppearance === "monthOnly"
  const inputType = dateOnly ? "date" : "datetime-local"

  // Convert ISO -> local-input format (YYYY-MM-DDTHH:mm) so the native
  // input renders the value the user expects in their timezone.
  const display = (() => {
    if (!value || typeof value !== "string") return ""
    try {
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ""
      const pad = (n: number) => String(n).padStart(2, "0")
      const yyyy = d.getFullYear()
      const mm = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      if (dateOnly) return `${yyyy}-${mm}-${dd}`
      const hh = pad(d.getHours())
      const min = pad(d.getMinutes())
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`
    } catch {
      return ""
    }
  })()

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={id}
    >
      <Input
        id={id}
        type={inputType}
        value={display}
        onChange={(e) => {
          const raw = e.target.value
          if (!raw) {
            setValue(null)
            return
          }
          // Treat the local-time picker output as local time, then
          // serialise to a UTC ISO string for storage.
          const d = new Date(raw)
          if (Number.isNaN(d.getTime())) return
          setValue(d.toISOString())
        }}
        aria-invalid={showError ? true : undefined}
        className="h-9 rounded-xl tabular-nums"
      />
    </FieldShell>
  )
}

export default ShadcnDateField
